import {
  forwardRef,
  PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useHotkeys } from "react-hotkeys-hook";
import { availableHotkeys } from "../../../configs/hotkeysConfig";
import { useTranslation } from "react-i18next";
import ButtonLikeAnchor from "../ButtonLikeAnchor";
import { FocusTrap } from "focus-trap-react";
import { LuX } from "react-icons/lu";
import { ErrorBoundary } from "react-error-boundary";

export type ModalProps = {
  open?: boolean;
  // Having this return false will prevent the modal from closing
  closeCallback?: () => boolean;
  /** If true, the first element in the modal automatically be focused. If false,
   * no element is initially focused */
  initialFocus?: false | string;
  header: string;
  classId: string;
  className?: string;
};

export type ModalHandle = {
  open: () => void;
  close?: () => void;
  isOpen?: () => boolean;
};

export const Modal = forwardRef<ModalHandle, PropsWithChildren<ModalProps>>(
  (
    { open = false, closeCallback, header, classId, className, children },
    ref,
  ) => {
    const [isOpen, setOpen] = useState(open);
    const close = useCallback(() => {
      if (closeCallback !== undefined && !closeCallback()) {
        // Don't close modal
        return;
      }
      setOpen(false);
    }, [closeCallback]);

    useImperativeHandle(
      ref,
      () => ({
        isOpen: () => isOpen,
        open: () => setOpen(true),
        close,
      }),
      [close, isOpen],
    );

	return ReactDOM.createPortal(
		isOpen &&
			<ModalContent
				close={close}
				header={header}
				classId={classId}
				className={className}
			>
				{children}
			</ModalContent>,
		document.body,
	);

});

const ModalContent = ({
	close,
	header,
	classId,
	className,
	children,
}: PropsWithChildren<{
	close: () => void,
	header: string,
	classId: string,
	className?: string,
}>) => {
	const { t } = useTranslation();

	useHotkeys(
		availableHotkeys.general.CLOSE_MODAL.sequence,
		close,
		{
			description:
				t(availableHotkeys.general.CLOSE_MODAL.description) ?? undefined,
		},
		[close],
	);

	return (
		<FocusTrap>
			<div>
				<div className="modal-animation modal-overlay" />
				<section
					id={classId}
					className={className ? className : "modal modal-animation"}
				>
					<header>
						<ButtonLikeAnchor
							className="close-modal"
							onClick={close}
							tabIndex={0}
						>
							<LuX />
						</ButtonLikeAnchor>
						<h2>
							{header}
						</h2>
					</header>
					<ErrorBoundary fallback={
						<div className="about">
							Something went wrong. Please close the modal and try again.
						</div>
					}>
						{children}
					</ErrorBoundary>
				</section>
			</div>
		</FocusTrap>
	);
};
